import {
  Component,
  HostListener,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "@core/auth/auth.service";
import { ConfigService } from "@core/config/app.config.service";
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { Store } from "@ngrx/store";
import { MENSAJES_NOTIFICACION } from "@shared/constants/apl-afiliacion.constants";
import { AfiliacionesBaseFunctions } from "@shared/functions/afiliaciones/afiliaciones-base.functions";
import { EmpleadorService } from "@shared/services/apl-afiliacion/empleador/empleador.service";
import { InactivityService } from "@shared/services/apl-afiliacion/login/inactivity.service";
import { AEL_CIFRADO_SERVICE_TOKEN } from "@utils/libs/cifrado/tokens";
import { AelCifradoService } from "ael-afiliacion-cifrado-utility";
import { NavigationService } from "app/core/navigation/navigation.service";
import { Navigation } from "app/core/navigation/navigation.types";
import { clearStore } from "app/store/actions/app.actions";
import { selectEmpleadorUser } from "app/store/selectors/empleador.selectors";
import { selectSeguridadUser } from "app/store/selectors/seguridad.selectors";
import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap
} from "rxjs";

@Component({
  selector: "modern-layout",
  templateUrl: "./modern.component.html",
  styleUrls: ["./modern.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
  private readonly _unsubscribeAll: Subject<any> = new Subject<any>();
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  private readonly _navigationService = inject(NavigationService);
  private readonly _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  private readonly _fuseNavigationService = inject(FuseNavigationService);
  private readonly _authService = inject(AuthService);
  private readonly _empleadorService = inject(EmpleadorService);
  private readonly snackBar: AfiliacionesBaseFunctions = inject(
    AfiliacionesBaseFunctions
  );
  private readonly _configService = inject(ConfigService);
  private readonly inactivityService: InactivityService =
    inject(InactivityService);
  private inactivitySubscription!: Subscription;
  empleadorOnboardingState: number;
  empleadorFocunState: number;
  private readonly destroy$ = new Subject<void>();
  isScreenSmall: boolean;
  isSmallScreen: boolean;
  navigation: Navigation;

  empleador: any;
  user: any;

  public menu: Array<any> = [];

  private readonly _suscripciones = new Subscription();

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.isSmallScreen = window.innerWidth < 768;
  }

  /**
   * Constructor
   */
  constructor(
    @Inject(AEL_CIFRADO_SERVICE_TOKEN)
    private readonly aelCifradoService: AelCifradoService,
    private readonly router: Router
  ) {
    this.isSmallScreen = window.innerWidth < 768;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._cargarMenu();
    this._dataUser()
      .pipe(
        take(1), // esperamos a que cargue el usuario
        switchMap(() => this.statesUSer()), // y recién ahí decidimos redirigir
        takeUntil(this.destroy$)
      )
      .subscribe();
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");
      });

    this.inactivityService.startMonitoring();

    this.inactivitySubscription =
      this.inactivityService.onInactivity$.subscribe(() => {
        this.snackBar.mostrarNotificacion(
          MENSAJES_NOTIFICACION[0].expira_sesion.mensaje,
          MENSAJES_NOTIFICACION[0].expira_sesion.duracion,
          MENSAJES_NOTIFICACION[0].expira_sesion.horizontalPosition,
          MENSAJES_NOTIFICACION[0].expira_sesion.verticalPosition
        );
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._suscripciones.unsubscribe();
    this.inactivitySubscription.unsubscribe();
    this.inactivityService.stopMonitoring();
    this.destroy$.next();
    this.destroy$.complete();
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  public _dataUser(): Observable<void> {
    return combineLatest([
      this._store.select(selectEmpleadorUser).pipe(
        filter((empleador) => empleador && Object.keys(empleador).length > 0),
        take(1)
      ),
      this._store.select(selectSeguridadUser).pipe(
        filter((user) => !!user && Object.keys(user).length > 0),
        take(1)
      ),
    ]).pipe(
      tap(([empleador, user]) => {
        this.empleador = empleador;
        this.empleador = {
          ...this.empleador,
          datos_basicos: {
            ...this.empleador.datos_basicos,
            no_id: this.aelCifradoService.decrypt(
              this.empleador.datos_basicos.no_id
            ),
          },
        };
        this.user = user;
      }),
      map(() => { })
    );
  }

  public statesUSer() {
    // <- sin :void, devolvemos Observable<void>
    try {
      if (this.empleador && Object.keys(this.empleador).length > 0) {
        return this._empleadorService
          .consultarEstadoFocunEmpleador(this.empleador.id_empleador)
          .pipe(
            take(1),
            tap((estados) => {
              this.empleadorFocunState =
                estados.data.estados_afiliacion.estado_configuracion_focun;
              this.empleadorOnboardingState =
                estados.data.estados_afiliacion.estado_onboarding;
            }),
            map(() => void 0),
            catchError((err) => {
              console.error("Error estados:", err);
              return of(void 0); // no rompas la cadena
            })
          );
      }
      // Si no hay empleador aún, no hacer nada pero mantener cadena
      return of(void 0);
    } catch (error) {
      console.error(error);
      return of(void 0);
    }
  }

  public irRuta(ruta: string): void {
    if (this._router.url !== ruta) this._router.navigate([ruta]);
  }

  private _cargarMenu(): void {
    this.menu = [
      {
        nombre: "Afiliaciones",
        rutas: [
          {
            nombre: "Afiliaciones",
            ruta: "/home/main-afiliacion",
          },
          {
            nombre: "Consulta",
            ruta: "/control-afiliacion/control_afiliacion",
          },
          {
            nombre: "Entidades",
            ruta: "/ans/empresas",
          },
          {
            nombre: "Histórico",
            ruta: "/home/historial-afiliaciones",
          },
          {
            nombre: "Reportes",
            ruta: "/home/apl-afiliacion-onboarding/bases-referencia",
          },
        ],
      },
      {
        nombre: "Planes",
        rutas: [
          {
            nombre: "Comprar plan",
            ruta: "/home/apl-afiliacion-onboarding/pagos",
          },
          {
            nombre: "Mis planes",
            ruta: "/home/apl-afiliacion-onboarding/pagos/mis-planes",
          },
        ],
      },
      {
        nombre: "Centro de ayuda",
        panelClass: "menu-panel-faq",
        rutas: [
          {
            nombre: "Preguntas frecuentes",
            ruta: "/home/preguntas-frecuentes",
            className: "menu-page-option-faq",
          },
        ],
      },
    ];
  }
  public signOut = (): void => {
    this._authService.signOut();
    this._store.dispatch(clearStore());
    window.open(this._configService.config.urlEmpleador, "_self");
  };

  truncarTexto(texto: string, maxLength: number): string {
    if (!texto) return "";
    return texto.length > maxLength ? texto.slice(0, maxLength) + "..." : texto;
  }

  onRutaKeydown(event: KeyboardEvent, ruta: string): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault(); // Evita scroll en espacio
      this.irRuta(ruta);
    }
  }

  onKey(event: KeyboardEvent) {
    event.preventDefault();
  }
   public obtenerUrlContactanos(){
    return !this._configService.config.production ? this._configService.config.urlContactanosQA : this._configService.config.urlContactanosPDN
  }
}
